import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

import { formatWeekRange, relativeWeekKey } from "../../helpers/weekReportFormat";

/**
 * O navegador de semana do relatório — `‹ Semana 36 · 31 ago – 6 set ›`.
 *
 * <p>🔴 <b>UMA peça, DUAS molduras</b> (CD, 2026-09-16 · redline v2 §1 e §5). No
 * desktop ele é encaixado à direita do cabeçalho da página; no mobile é o
 * PRIMEIRO CARD do corpo, de largura cheia. O que muda é o container — não o
 * comportamento, não a regra, não o que ele nomeia. Dois markups divergiriam na
 * primeira mudança, e a regra da seta é justamente a que não pode divergir.
 *
 * <p>🔴 <b>Por que no mobile ele sai do cabeçalho:</b> o cabeçalho mobile é
 * compartilhado — menu, migalha, título, avatar, igual em toda tela do portal —
 * e um controle que só esta tela tem faria o cabeçalho parecer instável em
 * TODAS as outras. No corpo ele vira o que é: um controle da página.
 *
 * <p>⚠️ <b>Ele NOMEIA a semana lida, sempre</b>, inclusive a que não fechou. É o
 * rótulo da página, não um índice do que existe.
 *
 * <p>✂️ <b>O sufixo <i>em curso</i> saiu</b> (PO, 2026-09-18). Ele vinha do
 * {@code isOpenWeek}, que é <i>"ainda não fechou"</i> — e isso inclui semana
 * FUTURA: a semana 39, que nem começou, aparecia como "em curso". O rótulo
 * afirmava andamento onde havia espera.
 * <p>⚠️ E ele era redundante com o corpo, que já diz o mesmo com precisão —
 * <i>"Relatório indisponível · Esta semana ainda não fechou"</i>. Duas fontes
 * para o mesmo fato, e a menos exata era a que ficava sempre visível.
 * <p>⚠️ As chaves {@code panel.report.week-open} saíram dos dois idiomas junto:
 * chave órfã já foi armadilha aqui — ela sobrevive à busca e sugere um consumidor
 * que não existe.
 *
 * <p>🔴 <b>AS DUAS GUARDAS, e elas não são simétricas.</b> A seta para a FRENTE
 * para na última semana FECHADA — o navegador não oferece semana sem fotografia.
 * A de TRÁS para no piso, e o piso depende da NATUREZA.
 *
 * <p>🔴 <b>Turma tem piso; aula particular não</b> (PO, 2026-09-17). Em turma o
 * piso é a primeira semana com {@code weekplan}: antes dela não há o que
 * fotografar, e um relatório ali não responde pergunta nenhuma. Em aula
 * particular a AUSÊNCIA é a medida — semana com zero alunos com plano é férias
 * ou vale de alunos, e é exatamente isso que o professor particular vai ler.
 * Fotografar zero faz sentido lá e não faz cá.
 *
 * <p>↺ <b>Esta peça já afirmou o contrário</b>, e a frase era: <i>"para trás não
 * há limite: semana passada sem fotografia é destino legítimo"</i>. Ela valia
 * para particular e foi aplicada às duas. Enquanto isso o
 * {@code PanelWeekReport} afirmava, no bloco do redirecionamento de piso, que
 * <i>"o navegador não oferece esse destino"</i> — duas regras escritas, em
 * arquivos diferentes, dizendo o oposto, e nenhuma das duas verdadeira na tela.
 *
 * <p>⚠️ {@code canGoPrev} chega RESOLVIDO do host, como o {@code canGoNext}: a
 * peça não conhece natureza nem piso, e não deve conhecer — ela é a mesma nas
 * duas telas, e é o host que tem o relatório.
 *
 * @param {string} variant `'header'` (desktop, encaixado) ou `'body'` (mobile, card)
 */
const PanelWeekNav = ({ weekNumber, weekStart, weekEnd,
                        canGoNext, canGoPrev = true, onPrev, onNext, variant = 'header' }) => {
	const { t } = useTranslation();
	/* 🔴 O RELATIVO QUALIFICA O NÚMERO, e por isso vive na linha dele (PO,
	   19/09) — `Semana 37 · anterior`, não `7 Set – 13 Set · semana anterior`.
	   Colado no que qualifica, e sem repetir a palavra "semana", que o título
	   já disse.
	   ⚠️ Só aqui, na NAVEGAÇÃO: rótulo relativo é dêixis e descreve o hoje de
	   quem lê. As frases que AFIRMAM fato continuam levando o intervalo, que
	   sobrevive a print, link e à virada da semana com a página aberta. */
	const rel = relativeWeekKey(weekStart);
	return (
		<div className={"pnl-wr-wknav" + (variant === 'body' ? " pnl-wr-wknav-body" : "")}>
			<button type="button" className="pnl-wr-wkarr"
					aria-label={t('panel.report.prev-week')}
					disabled={!canGoPrev} onClick={onPrev}>
				<FontAwesomeIcon icon={faChevronLeft} />
			</button>
			<div className="pnl-wr-week">
				<span className="pnl-wr-week-n">
					<Trans i18nKey="panel.report.week-n" values={{ n: weekNumber }}>Semana {{ n: weekNumber }}</Trans>
					{/* 🔴 MESMA fonte e MESMA tinta do número (PO, 19/09) — o rótulo
					    NÃO tem estilo próprio, e é isso que faz o desenho funcionar.
					    As duas primeiras tentativas erraram igual: tinta mais clara
					    aqui, e depois mudar para a linha das datas, ambas criando um
					    TERCEIRO nível de hierarquia numa caixa que só tem dois
					    (número forte, datas discretas). Quem subordina é a
					    PONTUAÇÃO, não a tipografia.
					    ⚠️ E a pontuação é o `·`, não o parêntese, mesmo o parêntese
					    sendo mais preciso — ele subordina ("Semana 37, que por acaso
					    é a anterior"), enquanto o `·` coordena. Ganhou a CONSISTÊNCIA
					    (PO): o `·` é o separador da casa e aparece três vezes na
					    seção de destaques desta mesma tela, então o olho já o lê como
					    "mesma coisa, outra faceta". */}
					{rel && <> · {t(`panel.report.week-rel-${rel}`)}</>}
				</span>
				<span className="pnl-wr-week-range">
					{formatWeekRange(weekStart, weekEnd)}
				</span>
			</div>
			<button type="button" className="pnl-wr-wkarr"
					aria-label={t('panel.report.next-week')}
					disabled={!canGoNext} onClick={onNext}>
				<FontAwesomeIcon icon={faChevronRight} />
			</button>
		</div>
	);
};

export default PanelWeekNav;
